import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  EquipmentKind,
  MuscleRegion,
  type EquipmentKind as EquipmentKindT,
  type MuscleRegion as MuscleRegionT,
} from "@heraion/shared";

import type { makeStyles } from "./catalogStyles";

// Dense filter control for a narrow pane (CatalogListView's `compactFilters`):
// a "Region ▾" button that toggles an in-flow vertical checklist. Active =
// count text + border + ✓ glyph on rows — never colour alone.
//
// Extracted from CatalogListView.tsx (SP-CATALOGLIST, 2026-07-27) to get that
// file under the 500-line cap. It was already gated behind the single
// `compactFilters` prop, so nothing else in the parent referenced it.
//
// `openAxis` moved IN with the markup rather than staying a prop: the parent
// declared it but only these three blocks ever read it, so it belongs here.
// Safe because `compactFilters` is a literal at its one caller
// (CatalogBrowse.tsx:63) and never toggles at runtime, so the child is not
// unmounted underneath an open checklist.
export function CatalogCompactFilterBar({
  styles,
  regions,
  equipment,
  toggleRegion,
  toggleEquipment,
  hasFilters,
  clearFilters,
}: {
  styles: ReturnType<typeof makeStyles>;
  regions: Set<MuscleRegionT>;
  equipment: Set<EquipmentKindT>;
  toggleRegion: (region: MuscleRegionT) => void;
  toggleEquipment: (kind: EquipmentKindT) => void;
  hasFilters: boolean;
  clearFilters: () => void;
}) {
  const { t } = useTranslation();
  // Which axis's option checklist is expanded (one at a time, in-flow below
  // the bar — no overflow-clipped popover).
  const [openAxis, setOpenAxis] = useState<"region" | "equipment" | null>(null);

  const filterButton = (
    axis: "region" | "equipment",
    label: string,
    count: number,
  ) => {
    const open = openAxis === axis;
    const active = count > 0 || open;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={() => setOpenAxis(open ? null : axis)}
        style={({ pressed }) => [
          styles.filterBtn,
          active && styles.filterBtnActive,
          pressed && styles.filterBtnPressed,
        ]}
      >
        <Text
          style={[styles.filterBtnText, active && styles.filterBtnTextActive]}
        >
          {label}
          {count > 0 ? ` · ${count}` : ""}
        </Text>
        <Text style={styles.filterBtnCaret}>{open ? "▴" : "▾"}</Text>
      </Pressable>
    );
  };

  const optionPanel = <T extends string,>(
    options: readonly T[],
    active: Set<T>,
    onToggle: (v: T) => void,
    renderLabel: (v: T) => string,
  ) => (
    <View style={styles.optionPanel}>
      {options.map((opt) => {
        const on = active.has(opt);
        return (
          <Pressable
            key={opt}
            accessibilityRole="button"
            accessibilityState={{ selected: on }}
            onPress={() => onToggle(opt)}
            style={({ pressed }) => [
              styles.optionRow,
              pressed && styles.optionRowPressed,
            ]}
          >
            <Text style={[styles.optionLabel, on && styles.optionLabelOn]}>
              {renderLabel(opt)}
            </Text>
            <Text style={styles.optionCheck}>{on ? "✓" : ""}</Text>
          </Pressable>
        );
      })}
    </View>
  );

  return (
    <>
      <View style={styles.filterBar}>
        {filterButton("region", t("catalog.region"), regions.size)}
        {filterButton("equipment", t("catalog.equipment"), equipment.size)}
        {hasFilters ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              clearFilters();
              setOpenAxis(null);
            }}
            style={({ pressed }) => [
              styles.clearInline,
              pressed && styles.clearFiltersPressed,
            ]}
          >
            <Text style={styles.clearFiltersText}>
              {t("mobile.logger.pickerClearFilters")}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {openAxis === "region"
        ? optionPanel(MuscleRegion.options, regions, toggleRegion, (r) =>
            t(`catalog.region_${r}`, { defaultValue: r }),
          )
        : null}
      {openAxis === "equipment"
        ? optionPanel(
            EquipmentKind.options,
            equipment,
            toggleEquipment,
            (k) => t(`catalog.equipment_${k}`, { defaultValue: k }),
          )
        : null}
    </>
  );
}
